import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { AlertTriangle, RotateCcw, Upload } from "lucide-mui";
import { formatNumber } from "../../../utils/format.ts";
import { keys } from "../../../api/queryKeys.ts";
import { api } from "../../../api/client";
import type { CsvImport } from "../../../api/client";
import FormattedDate from "../../../components/FormattedDate";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import Panel from "../../../components/Panel.tsx";
import StatusChip from "../../../components/StatusChip.tsx";
import PageHeader from "../../../components/PageHeader.tsx";

const IMPORT_SOURCES = [
  {
    value: "clay_csv",
    label: "Clay CSV",
    description:
      "Company export from Clay. Columns: name, domain, country, location, industry, org_form, size, linkedin_url. Upserts master_companies.",
  },
  {
    value: "clay_contacts_csv",
    label: "Clay contacts CSV",
    description:
      "People export from Clay. Columns: first_name, last_name, full_name, job_title, company_name, city, state, country, linkedin_url. Creates/links master_contacts.",
  },
  {
    value: "domain_lookup",
    label: "Domain lookup",
    description:
      "Org number → domain lookup. Columns: org_id_normalized, domain, country (default NO). Matches master_companies by (country, org_number); only fills domain if empty.",
  },
  {
    value: "name_domain_csv",
    label: "Name→domain CSV",
    description:
      "Company name → domain lookup. Columns: company_name, domain, country (default NO). Matches master_companies by normalized legal name (strips AS/A/S); use domain_lookup instead if you have org numbers.",
  },
  {
    value: "ats_feed_parquet",
    label: "ATS feed Parquet",
    description:
      "Parquet file (not CSV). Columns: ats, domain, feed_url, is_subdomain, discovery_source, cc_index. Upserts ats_feeds rows.",
  },
  {
    value: "norwegian_ownership_csv",
    label: "Norwegian ownership CSV",
    description:
      "Aksjonærregisteret shareholder-ownership export. Requires a snapshot year. Upserts shareholdings and ownership entity_edges.",
  },
];

const NOW_YEAR = new Date().getFullYear();
const MAX_SNAPSHOT_YEAR = NOW_YEAR + 1;
const MIN_SNAPSHOT_YEAR = 2000;

function isStale(row: CsvImport): boolean {
  if (row.status !== "running") return false;
  const updatedAt = new Date(row.updated_at).getTime();
  const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
  return updatedAt < fiveMinutesAgo;
}

function progressLabel(row: CsvImport): string | null {
  if (row.status !== "running") return null;
  const p = row.progress as Record<string, unknown> | null;
  if (!p?.phase) return "Starting...";
  if (p.phase === "importing") {
    return p.total
      ? `Importing: ${formatNumber(p.processed as number)}/${formatNumber(p.total as number)} rows`
      : `Importing: ${formatNumber(p.processed as number)} rows`;
  }
  if (p.phase === "merging") {
    return `Merging: ${formatNumber(p.records_merged as number)} records`;
  }
  return null;
}

export default function ImportsPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [source, setSource] = useState("");
  const [snapshotYear, setSnapshotYear] = useState<string>(String(NOW_YEAR));

  const requiresSnapshotYear = source === "norwegian_ownership_csv";
  const parsedSnapshotYear = Number(snapshotYear);
  const snapshotYearValid =
    Number.isInteger(parsedSnapshotYear) &&
    parsedSnapshotYear >= MIN_SNAPSHOT_YEAR &&
    parsedSnapshotYear <= MAX_SNAPSHOT_YEAR;

  const { data: imports, isLoading } = useQuery({
    queryKey: keys.csvImport.list(),
    queryFn: () => api.getCsvImports(),
    meta: { live: true },
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) =>
      api.uploadCsvImport(
        file,
        source,
        requiresSnapshotYear ? { snapshotYear: parsedSnapshotYear } : undefined,
      ),
    onSuccess: () => {
      toast.success("CSV upload started");
      queryClient.invalidateQueries({ queryKey: keys.csvImport.list() });
    },
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => api.retryCsvImport(id),
    onSuccess: () => {
      toast.success("Import retry started");
      queryClient.invalidateQueries({ queryKey: keys.csvImport.list() });
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadMutation.mutate(file);
    }
    e.target.value = "";
  };

  return (
    <Box>
      <PageHeader
        title="Imports"
        description="Upload company, contact and ownership files into the platform and follow each import"
      />
      <Panel title="Upload import" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
          <TextField
            select
            label="Source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            size="small"
            sx={{ minWidth: 220 }}
            slotProps={{
              inputLabel: { shrink: true },
              select: {
                displayEmpty: true,
                renderValue: (value) =>
                  IMPORT_SOURCES.find((s) => s.value === value)?.label ?? "Select source...",
              },
            }}
          >
            <MenuItem value="" disabled>
              Select source...
            </MenuItem>
            {IMPORT_SOURCES.map((s) => (
              <MenuItem
                key={s.value}
                value={s.value}
                sx={{ whiteSpace: "normal" }}
              >
                <ListItemText
                  primary={s.label}
                  secondary={s.description}
                  slotProps={{
                    secondary: {
                      variant: "caption",
                      sx: { whiteSpace: "normal" },
                    },
                  }}
                />
              </MenuItem>
            ))}
          </TextField>
          {requiresSnapshotYear && (
            <TextField
              label="Snapshot year"
              type="number"
              size="small"
              value={snapshotYear}
              onChange={(e) => setSnapshotYear(e.target.value)}
              error={!snapshotYearValid}
              helperText={
                snapshotYearValid
                  ? null
                  : `Year must be between ${MIN_SNAPSHOT_YEAR} and ${MAX_SNAPSHOT_YEAR}`
              }
              slotProps={{
                htmlInput: {
                  min: MIN_SNAPSHOT_YEAR,
                  max: MAX_SNAPSHOT_YEAR,
                  step: 1,
                },
              }}
              sx={{ width: 160 }}
            />
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.parquet"
            hidden
            onChange={handleFileChange}
          />
          <Button
            variant="contained"
            startIcon={<Upload />}
            loading={uploadMutation.isPending}
            loadingPosition="start"
            onClick={() => fileInputRef.current?.click()}
            disabled={!source || (requiresSnapshotYear && !snapshotYearValid)}
          >
            Upload file
          </Button>
        </Stack>
      </Panel>
      <TableCard loading={isLoading}>
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>File</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Rows</TableCell>
              <TableCell align="right">Imported</TableCell>
              <TableCell align="right">Duplicates</TableCell>
              <TableCell>Created</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {!imports?.length ? (
              <EmptyTableRow colSpan={8}>No imports yet</EmptyTableRow>
            ) : (
              imports.map((row) => {
                const sourceLabel =
                  IMPORT_SOURCES.find((s) => s.value === row.source)?.label ??
                  row.source;
                const snapshotYearForRow =
                  typeof row.metadata?.snapshot_year === "number"
                    ? (row.metadata.snapshot_year as number)
                    : null;
                return (
                <TableRow key={row.id}>
                  <TableCell sx={{ maxWidth: 280, wordBreak: "break-all" }}>{row.file_name}</TableCell>
                  <TableCell>
                    {sourceLabel}
                    {snapshotYearForRow != null && (
                      <Typography variant="caption" sx={{ color: "text.secondary", ml: 1 }}>
                        ({snapshotYearForRow})
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} sx={{
                      alignItems: "center"
                    }}>
                      <StatusChip kind="job" value={row.status} />
                      {row.status === "running" && (
                        <Typography variant="caption" sx={{
                          color: "text.secondary"
                        }}>
                          {progressLabel(row) ?? "Starting..."}
                        </Typography>
                      )}
                      {isStale(row) && (
                        <Tooltip title="Import may be stalled — no updates for 5+ minutes" arrow>
                          <AlertTriangle color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                      {row.status === "failed" && row.error && (
                        <Tooltip title={row.error} arrow>
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                            }}
                          >
                            {row.error}
                          </Typography>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    {row.row_count != null ? formatNumber(row.row_count) : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {row.imported_count != null ? formatNumber(row.imported_count) : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {row.duplicate_count != null ? formatNumber(row.duplicate_count) : "—"}
                  </TableCell>
                  <TableCell>
                    <FormattedDate value={row.created_at} />
                  </TableCell>
                  <TableCell align="right">
                    {row.status === "failed" && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<RotateCcw />}
                        onClick={() => retryMutation.mutate(row.id)}
                        loading={retryMutation.isPending && retryMutation.variables === row.id}
                        disabled={retryMutation.isPending}
                      >
                        Retry
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </ScrollTable>
      </TableCard>
    </Box>
  );
}
